import piexif from 'piexifjs';
import logger from './logger';

const JPEG_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg']);
const PNG_IMAGE_TYPES = new Set(['image/png']);
const SUPPORTED_IMAGE_TYPES = new Set([...JPEG_IMAGE_TYPES, ...PNG_IMAGE_TYPES]);

const UTF16_TERMINATOR = 0;
const GEOTAG_CHUNK_KEYWORD = 'VisibeenGeotag';
const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder('utf-8');

const rationalPairToDecimal = (pair) => {
  if (!pair || !Array.isArray(pair) || pair.length !== 2) return null;
  const [numerator, denominator] = pair;
  if (!denominator) return null;
  return numerator / denominator;
};

const dmsComponentsToDecimal = (dms, ref) => {
  if (!Array.isArray(dms) || dms.length !== 3) return null;
  const degrees = rationalPairToDecimal(dms[0]);
  const minutes = rationalPairToDecimal(dms[1]);
  const seconds = rationalPairToDecimal(dms[2]);
  if ([degrees, minutes, seconds].some((value) => typeof value !== 'number' || Number.isNaN(value))) {
    return null;
  }
  let decimal = degrees + minutes / 60 + seconds / 3600;
  if (ref === 'S' || ref === 'W') {
    decimal *= -1;
  }
  return decimal;
};

const ensureExifSkeleton = (exif) => ({
  '0th': exif?.['0th'] || {},
  Exif: exif?.Exif || {},
  GPS: exif?.GPS || {},
  Interop: exif?.Interop || {},
  '1st': exif?.['1st'] || {},
  thumbnail: exif?.thumbnail || null
});

const toRational = (value) => {
  if (Number.isNaN(Number(value))) {
    return [0, 1];
  }
  const precision = 1000000;
  const numerator = Math.round(Number(value) * precision);
  const denominator = precision;
  return [numerator, denominator];
};

const decimalToDms = (decimal) => {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesFloat = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;

  return [
    [degrees, 1],
    [minutes, 1],
    toRational(seconds)
  ];
};

const stringToUtf16Bytes = (value) => {
  const array = [];
  for (let i = 0; i < value.length; i += 1) {
    array.push(value.charCodeAt(i));
    array.push(0);
  }
  array.push(UTF16_TERMINATOR);
  array.push(UTF16_TERMINATOR);
  return array;
};

const utf16BytesToString = (input) => {
  if (!input) return '';
  const bytes = Array.isArray(input) ? input : Array.from(input);
  const chars = [];
  for (let i = 0; i < bytes.length; i += 2) {
    const first = bytes[i];
    const second = bytes[i + 1];
    if ((first === 0 || first === undefined) && (second === 0 || second === undefined)) {
      break;
    }
    if (first !== 0 && first !== undefined) {
      chars.push(String.fromCharCode(first));
    }
  }
  return chars.join('').trim();
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
  reader.readAsDataURL(file);
});

const dataUrlToFile = (dataUrl, fileName, mimeType) => {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const length = binary.length;
  const buffer = new ArrayBuffer(length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < length; i += 1) {
    view[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([buffer], { type: mimeType });
  return new File([blob], fileName, { type: mimeType, lastModified: Date.now() });
};

const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const length = binary.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const createCrcTable = () => {
  const table = [];
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      if (c & 1) {
        c = 0xedb88320 ^ (c >>> 1);
      } else {
        c >>>= 1;
      }
    }
    table[i] = c >>> 0;
  }
  return table;
};

const CRC_TABLE = createCrcTable();

const crc32 = (bytes) => {
  let crc = -1;
  for (let i = 0; i < bytes.length; i += 1) {
    const byte = bytes[i];
    crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ byte) & 0xff];
  }
  return (crc ^ -1) >>> 0;
};

const writeUint32 = (value) => {
  const result = new Uint8Array(4);
  result[0] = (value >>> 24) & 0xff;
  result[1] = (value >>> 16) & 0xff;
  result[2] = (value >>> 8) & 0xff;
  result[3] = value & 0xff;
  return result;
};

const createPngChunk = (type, data) => {
  const typeBytes = textEncoder.encode(type);
  const crcPayload = new Uint8Array(typeBytes.length + data.length);
  crcPayload.set(typeBytes, 0);
  crcPayload.set(data, typeBytes.length);
  const crcValue = crc32(crcPayload);
  const chunk = new Uint8Array(8 + data.length + 4);
  chunk.set(writeUint32(data.length), 0);
  chunk.set(typeBytes, 4);
  chunk.set(data, 8);
  chunk.set(writeUint32(crcValue), 8 + data.length);
  return chunk;
};

const injectPngChunk = (bytes, type, data, options = {}) => {
  if (!isPngSignature(bytes)) {
    throw new Error('Invalid PNG signature');
  }

  const chunk = createPngChunk(type, data);
  const segments = [bytes.slice(0, 8)];
  let offset = 8;
  let inserted = false;

  const { shouldReplaceChunk, insertBeforeChunkTypes = [] } = options;

  while (offset < bytes.length) {
    const length =
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3];
    const typeStart = offset + 4;
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    const chunkEnd = dataEnd + 4;
    const chunkType = String.fromCharCode(
      bytes[typeStart],
      bytes[typeStart + 1],
      bytes[typeStart + 2],
      bytes[typeStart + 3]
    );

    const chunkBytes = bytes.slice(offset, chunkEnd);
    const dataSlice = bytes.subarray(dataStart, dataEnd);
    const shouldReplace =
      chunkType === type && (typeof shouldReplaceChunk !== 'function' || shouldReplaceChunk(dataSlice));

    if (shouldReplace) {
      if (!inserted) {
        segments.push(chunk);
        inserted = true;
      }
      offset = chunkEnd;
      continue;
    }

    if (!inserted && insertBeforeChunkTypes.includes(chunkType)) {
      segments.push(chunk);
      inserted = true;
    }

    if (chunkType === 'IEND' && !inserted) {
      segments.push(chunk);
      inserted = true;
    }

    segments.push(chunkBytes);
    offset = chunkEnd;
  }

  if (!inserted) {
    segments.push(chunk);
  }

  const totalLength = segments.reduce((sum, segment) => sum + segment.length, 0);
  const result = new Uint8Array(totalLength);
  let writeOffset = 0;
  segments.forEach((segment) => {
    result.set(segment, writeOffset);
    writeOffset += segment.length;
  });
  return result;
};

const isPngSignature = (bytes) => PNG_SIGNATURE.every((value, index) => bytes[index] === value);

const parseItxtChunk = (data) => {
  let offset = 0;
  const readSection = () => {
    const end = data.indexOf(0, offset);
    if (end === -1) {
      const value = textDecoder.decode(data.subarray(offset));
      offset = data.length;
      return value;
    }
    const value = textDecoder.decode(data.subarray(offset, end));
    offset = end + 1;
    return value;
  };

  const keyword = readSection();
  const compressionFlag = data[offset] ?? 0;
  offset += 1;
  const compressionMethod = data[offset] ?? 0;
  offset += 1;
  const languageTag = readSection();
  const translatedKeyword = readSection();
  const textBytes = data.subarray(offset);

  return {
    keyword,
    compressionFlag,
    compressionMethod,
    languageTag,
    translatedKeyword,
    textBytes
  };
};

const getGeotagChunkInfo = (data) => {
  try {
    const parsed = parseItxtChunk(data);
    if (parsed.keyword === GEOTAG_CHUNK_KEYWORD) {
      return parsed;
    }
  } catch (error) {
    logger.warn('[Geotag] Failed to parse PNG geotag chunk:', error);
  }
  return null;
};

const isGeotagChunk = (data) => Boolean(getGeotagChunkInfo(data));

const buildGeotagChunkData = (metadata) => {
  const keywordBytes = textEncoder.encode(GEOTAG_CHUNK_KEYWORD);
  const textBytes = textEncoder.encode(JSON.stringify(metadata));
  const data = new Uint8Array(keywordBytes.length + 5 + textBytes.length);
  let offset = 0;

  data.set(keywordBytes, offset);
  offset += keywordBytes.length;

  data[offset] = 0;
  offset += 1;
  data[offset] = 0;
  offset += 1;
  data[offset] = 0;
  offset += 1;
  data[offset] = 0;
  offset += 1;
  data[offset] = 0;
  offset += 1;

  data.set(textBytes, offset);
  return data;
};

const decodeGeotagChunkText = (chunkInfo) => {
  if (!chunkInfo) return null;
  if (chunkInfo.compressionFlag) {
    logger.warn('[Geotag] Compressed iTXt chunks are not supported for geotag metadata.');
    return null;
  }
  return textDecoder.decode(chunkInfo.textBytes || new Uint8Array());
};

const readGeotagMetadataFromPngBytes = (bytes) => {
  if (!isPngSignature(bytes)) return null;

  let offset = 8;
  while (offset < bytes.length) {
    const length =
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3];
    const typeStart = offset + 4;
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    const chunkEnd = dataEnd + 4;
    const chunkType = String.fromCharCode(
      bytes[typeStart],
      bytes[typeStart + 1],
      bytes[typeStart + 2],
      bytes[typeStart + 3]
    );

    if (chunkType === 'iTXt') {
      const chunkInfo = getGeotagChunkInfo(bytes.subarray(dataStart, dataEnd));
      if (chunkInfo) {
        const text = decodeGeotagChunkText(chunkInfo);
        if (text) {
          try {
            return JSON.parse(text);
          } catch (parseError) {
            logger.warn('[Geotag] Failed to parse geotag metadata from PNG chunk:', parseError);
            return null;
          }
        }
      }
    }

    offset = chunkEnd;
  }

  return null;
};

const insertGeotagChunkIntoPng = (bytes, metadata) => {
  const data = buildGeotagChunkData(metadata);
  return injectPngChunk(bytes, 'iTXt', data, { shouldReplaceChunk: (chunkData) => isGeotagChunk(chunkData) });
};

const insertExifChunkIntoPng = (bytes, exifBytes) =>
  injectPngChunk(bytes, 'eXIf', exifBytes, { insertBeforeChunkTypes: ['IDAT'] });


const applyPngGeotag = (dataUrl, metadata, file) => {
  const base64 = dataUrl.split(',')[1];
  let bytes = base64ToUint8Array(base64);

  const exifStruct = ensureExifSkeleton({});
  populateExifMetadata(exifStruct, metadata);
  const exifBytes = piexif.dump(exifStruct);

  bytes = insertExifChunkIntoPng(bytes, exifBytes);
  bytes = insertGeotagChunkIntoPng(bytes, metadata);

  const updatedBase64 = uint8ArrayToBase64(bytes);
  const updatedDataUrl = `data:${file.type};base64,${updatedBase64}`;
  const updatedFile = dataUrlToFile(updatedDataUrl, file.name, file.type);
  return { applied: true, file: updatedFile };
};

const sanitizeNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const converted = Number(value);
  if (Number.isNaN(converted)) return null;
  return converted;
};

const populateExifMetadata = (exifStruct, normalized) => {
  const { latitude, longitude, description, keywords } = normalized;

  exifStruct.GPS[piexif.GPSIFD.GPSLatitudeRef] = latitude >= 0 ? 'N' : 'S';
  exifStruct.GPS[piexif.GPSIFD.GPSLatitude] = decimalToDms(latitude);
  exifStruct.GPS[piexif.GPSIFD.GPSLongitudeRef] = longitude >= 0 ? 'E' : 'W';
  exifStruct.GPS[piexif.GPSIFD.GPSLongitude] = decimalToDms(longitude);
  exifStruct.GPS[piexif.GPSIFD.GPSMapDatum] = 'WGS-84';

  if (description) {
    exifStruct['0th'][piexif.ImageIFD.ImageDescription] = description.slice(0, 254);
  } else {
    delete exifStruct['0th'][piexif.ImageIFD.ImageDescription];
  }

  if (keywords.length > 0) {
    const keywordString = keywords.slice(0, 10).join('; ');
    exifStruct['0th'][piexif.ImageIFD.XPKeywords] = stringToUtf16Bytes(keywordString);
  } else {
    delete exifStruct['0th'][piexif.ImageIFD.XPKeywords];
  }
};

export const canEmbedGeotag = (file) => {
  if (!file?.type) return false;
  return SUPPORTED_IMAGE_TYPES.has(file.type.toLowerCase());
};

export const hasCoordinates = (metadata) => {
  if (!metadata) return false;
  const lat = sanitizeNumber(metadata.latitude ?? metadata.lat);
  const lng = sanitizeNumber(metadata.longitude ?? metadata.lng);
  return typeof lat === 'number' && typeof lng === 'number';
};

export const normalizeGeotagMetadata = (metadata) => {
  const source = metadata ?? {};
  const latitude = sanitizeNumber(source.latitude ?? source.lat);
  const longitude = sanitizeNumber(source.longitude ?? source.lng);
  const keywords = Array.isArray(source.keywords)
    ? source.keywords.map((keyword) => String(keyword || '').trim()).filter(Boolean)
    : [];
  const description = source.description ? String(source.description).trim() : '';

  return {
    latitude,
    longitude,
    keywords,
    description
  };
};

const parseKeywordsString = (value) =>
  String(value || '')
    .split(/;\s*|,\s*/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);

const extractExifGeotagMetadata = (exifStruct) => {
  if (!exifStruct?.GPS) return null;
  const lat = dmsComponentsToDecimal(
    exifStruct.GPS[piexif.GPSIFD.GPSLatitude],
    exifStruct.GPS[piexif.GPSIFD.GPSLatitudeRef]
  );
  const lng = dmsComponentsToDecimal(
    exifStruct.GPS[piexif.GPSIFD.GPSLongitude],
    exifStruct.GPS[piexif.GPSIFD.GPSLongitudeRef]
  );
  if (lat == null || lng == null) return null;

  const description = exifStruct['0th']?.[piexif.ImageIFD.ImageDescription] || '';
  const keywordsField = exifStruct['0th']?.[piexif.ImageIFD.XPKeywords];
  const keywordsString = typeof keywordsField === 'string' ? keywordsField : utf16BytesToString(keywordsField);

  return normalizeGeotagMetadata({
    latitude: lat,
    longitude: lng,
    description,
    keywords: keywordsString ? parseKeywordsString(keywordsString) : []
  });
};

const extractPngGeotagMetadata = (bytes) => {
  const metadata = readGeotagMetadataFromPngBytes(bytes);
  if (!metadata) return null;
  return normalizeGeotagMetadata(metadata);
};

const readExifChunkFromPngBytes = (bytes) => {
  if (!isPngSignature(bytes)) return null;

  let offset = 8;
  while (offset < bytes.length) {
    const length =
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3];
    const typeStart = offset + 4;
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    const chunkEnd = dataEnd + 4;
    const chunkType = String.fromCharCode(
      bytes[typeStart],
      bytes[typeStart + 1],
      bytes[typeStart + 2],
      bytes[typeStart + 3]
    );

    if (chunkType === 'eXIf') {
      return bytes.slice(dataStart, dataEnd);
    }

    offset = chunkEnd;
  }

  return null;
};

const buildJpegDataUrlFromExifBytes = (exifBytes) => {
  if (!exifBytes || exifBytes.length === 0) return null;
  const app1Length = exifBytes.length + 2;
  const jpegBytes = new Uint8Array(exifBytes.length + 8);
  let offset = 0;
  jpegBytes[offset++] = 0xff;
  jpegBytes[offset++] = 0xd8; // SOI
  jpegBytes[offset++] = 0xff;
  jpegBytes[offset++] = 0xe1; // APP1
  jpegBytes[offset++] = (app1Length >> 8) & 0xff;
  jpegBytes[offset++] = app1Length & 0xff;
  jpegBytes.set(exifBytes, offset);
  offset += exifBytes.length;
  jpegBytes[offset++] = 0xff;
  jpegBytes[offset++] = 0xd9; // EOI

  return `data:image/jpeg;base64,${uint8ArrayToBase64(jpegBytes)}`;
};

const extractExifMetadataFromChunk = (exifBytes) => {
  if (!exifBytes || exifBytes.length === 0) return null;
  try {
    const dataUrl = buildJpegDataUrlFromExifBytes(exifBytes);
    if (!dataUrl) return null;
    const exif = piexif.load(dataUrl);
    return extractExifGeotagMetadata(exif);
  } catch (error) {
    logger.warn('[Geotag] Failed to parse EXIF metadata from PNG chunk:', error);
    return null;
  }
};

const extractExifMetadataFromPngBytes = (bytes) => {
  const chunk = readExifChunkFromPngBytes(bytes);
  if (!chunk) return null;
  return extractExifMetadataFromChunk(chunk);
};

export const inspectGeotagMetadata = async (file) => {
  if (!file) {
    return { success: false, reason: 'Missing file reference' };
  }
  const type = file.type?.toLowerCase();
  if (!type) {
    return { success: false, reason: 'Unknown file type' };
  }

  try {
    const dataUrl = await fileToDataUrl(file);
    if (JPEG_IMAGE_TYPES.has(type)) {
      try {
        const exif = piexif.load(dataUrl);
        const embedded = extractExifGeotagMetadata(exif);
        if (embedded) {
          return { success: true, format: 'jpeg', embedded };
        }
        return { success: false, format: 'jpeg', reason: 'No GPS metadata present' };
      } catch (error) {
        logger.warn('[Geotag] Unable to inspect JPEG metadata:', error);
        return { success: false, format: 'jpeg', reason: 'Unable to parse EXIF' };
      }
    }

    if (PNG_IMAGE_TYPES.has(type)) {
      const base64 = dataUrl.split(',')[1];
      const bytes = base64ToUint8Array(base64);
      const embedded = extractPngGeotagMetadata(bytes) || extractExifMetadataFromPngBytes(bytes);
      if (embedded) {
        return { success: true, format: 'png', embedded };
      }
      return { success: false, format: 'png', reason: 'No geotag metadata detected' };
    }

    return { success: false, format: type, reason: 'Unsupported file type for geotag inspection' };
  } catch (error) {
    logger.warn('[Geotag] Failed to inspect geotag metadata:', error);
    return { success: false, reason: error.message };
  }
};

export const applyGeotagMetadata = async (file, metadata) => {
  if (!file || !metadata) {
    return { applied: false, file, reason: 'Missing file or metadata' };
  }

  const normalized = normalizeGeotagMetadata(metadata);
  if (!hasCoordinates(normalized)) {
    return { applied: false, file, reason: 'Missing coordinates' };
  }

  if (!canEmbedGeotag(file)) {
    logger.warn('[Geotag] File type does not support EXIF metadata injection:', file.type);
    return { applied: false, file, reason: 'Unsupported file type' };
  }

  try {
    const dataUrl = await fileToDataUrl(file);
    const lowerType = file.type.toLowerCase();
    const isJpeg = JPEG_IMAGE_TYPES.has(lowerType);
    const isPng = PNG_IMAGE_TYPES.has(lowerType);

    if (isJpeg) {
      let exif = {};
      try {
        exif = piexif.load(dataUrl);
      } catch (error) {
        logger.warn('[Geotag] Failed to load existing EXIF metadata, creating fresh structure:', error);
      }

      const exifStruct = ensureExifSkeleton(exif);
      populateExifMetadata(exifStruct, normalized);
      const exifBytes = piexif.dump(exifStruct);
      const newDataUrl = piexif.insert(exifBytes, dataUrl);
      const updatedFile = dataUrlToFile(newDataUrl, file.name, file.type);
      return { applied: true, file: updatedFile };
    }

    if (isPng) {
      return applyPngGeotag(dataUrl, normalized, file);
    }

    logger.warn('[Geotag] File type does not support EXIF metadata injection:', file.type);
    return { applied: false, file, reason: 'Unsupported file type' };
  } catch (error) {
    logger.error('[Geotag] Failed to embed metadata into image:', error);
    return { applied: false, file, error };
  }
};

export const buildKeywordSuggestions = (sources = []) => {
  const set = new Set();
  sources
    .flat()
    .filter(Boolean)
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .forEach((keyword) => {
      set.add(keyword.toLowerCase());
    });
  return Array.from(set).map((keyword) => keyword.replace(/(^.|\s.)/g, (match) => match.toUpperCase()));
};

export default {
  canEmbedGeotag,
  hasCoordinates,
  normalizeGeotagMetadata,
  applyGeotagMetadata,
  buildKeywordSuggestions,
  inspectGeotagMetadata
};
